import { useMemo, useState } from 'react';
import { cn } from '@/utils/cn';
import type { SyncJobRow } from '../types';

interface SyncJobDetailRowProps {
  row: SyncJobRow;
}

const DETAIL_PAGE_SIZE = 5;

interface DetailColumn<T> {
  key: keyof T & string;
  header: string;
}

function DetailTable<T extends object>({
  rows,
  columns,
  ariaLabel,
}: {
  rows: readonly T[];
  columns: readonly DetailColumn<T>[];
  ariaLabel: string;
}) {
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(rows.length / DETAIL_PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * DETAIL_PAGE_SIZE;
  const pageRows = useMemo(
    () => rows.slice(start, start + DETAIL_PAGE_SIZE),
    [rows, start],
  );

  if (rows.length === 0) {
    return (
      <p className="px-2 py-3 text-sm text-cool-gray-11">No detail records.</p>
    );
  }

  return (
    <div className="space-y-2">
      <div className="overflow-auto border border-cool-gray-2">
        <table className="w-full min-w-[480px] border-collapse" aria-label={ariaLabel}>
          <thead className="bg-gray-50">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  className="px-3 h-10 text-left text-xs font-semibold text-cool-gray-11 whitespace-nowrap"
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row, idx) => (
              <tr key={idx} className="hover:bg-gray-50">
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className="px-3 h-10 text-sm text-black border-t border-cool-gray-2 whitespace-nowrap"
                  >
                    {String(row[col.key] ?? '')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 ? (
        <div className="flex items-center justify-end gap-2 text-xs text-cool-gray-11">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={safePage <= 1}
            className={cn(
              'inline-flex h-7 items-center px-2 border border-cool-gray-2',
              safePage <= 1 && 'opacity-40 cursor-not-allowed',
            )}
            aria-label="Previous detail page"
          >
            Prev
          </button>
          <span aria-live="polite">
            Page {safePage} of {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={safePage >= totalPages}
            className={cn(
              'inline-flex h-7 items-center px-2 border border-cool-gray-2',
              safePage >= totalPages && 'opacity-40 cursor-not-allowed',
            )}
            aria-label="Next detail page"
          >
            Next
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function SyncJobDetailRow({ row }: SyncJobDetailRowProps) {
  if (row.jobName === 'EventSync') {
    return (
      <DetailTable
        rows={row.eventSyncRes ?? []}
        ariaLabel="Event sync details"
        columns={[
          { key: 'eventStartDate', header: 'Event Start Date' },
          { key: 'eventEndDate', header: 'Event End Date' },
          { key: 'status', header: 'Status' },
          { key: 'location', header: 'Location' },
        ]}
      />
    );
  }
  if (row.jobName === 'ContactSync') {
    return (
      <DetailTable
        rows={row.conactSyncRes ?? []}
        ariaLabel="Contact sync details"
        columns={[
          { key: 'firstName', header: 'First Name' },
          { key: 'lastName', header: 'Last Name' },
          { key: 'gender', header: 'Gender' },
          { key: 'email', header: 'Email' },
        ]}
      />
    );
  }
  if (row.jobName === 'AttendeeSync') {
    return (
      <DetailTable
        rows={row.attendeeSyncRes ?? []}
        ariaLabel="Attendee sync details"
        columns={[
          { key: 'confirmationNumber', header: 'Confirmation Number' },
          { key: 'attendeeType', header: 'Attendee Type' },
          { key: 'invitationId', header: 'Invitation Id' },
          { key: 'accomodationStartDate', header: 'Accomodation Start Date' },
          { key: 'accomodationEndDate', header: 'Accomodation End Date' },
          { key: 'hotelName', header: 'Hotel Name' },
          { key: 'registeredAt', header: 'Registered At' },
        ]}
      />
    );
  }
  return (
    <p className="px-2 py-3 text-sm text-cool-gray-11">
      No detail view available for {row.jobName}.
    </p>
  );
}
